import { baseApi } from "./baseApi";

// Stock alert types
export type StockAlertLevel = "OUT_OF_STOCK" | "CRITICAL" | "LOW" | "NORMAL";

export interface ProductStockAlert {
  id: string;
  name: string;
  image: string;
  stockQuantity: number;
  inStock: boolean;
  alertLevel: StockAlertLevel;
  category: {
    id: string;
    name: string;
  } | null;
}

export interface StockSummary {
  totalProducts: number;
  inStock: number;
  outOfStock: number;
  lowStock: number;
  criticalStock: number;
}

export interface StockThresholds {
  lowStockThreshold: number;
  criticalStockThreshold: number;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface StockAlertFilters {
  page?: number;
  limit?: number;
  includeOutOfStock?: boolean;
}

export interface PaginatedStockAlertsResponse {
  success: boolean;
  data: ProductStockAlert[];
  pagination: PaginationMeta;
}

export interface StockUpdateData {
  productId: string;
  stockQuantity: number;
}

/**
 * Stock Alerts API slice
 */
export const stockAlertsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get stock summary statistics
    getStockSummary: builder.query<{ success: boolean; data: StockSummary }, void>({
      query: () => ({
        url: "/stock/summary",
        credentials: "include",
      }),
      providesTags: ["Stock"],
    }),

    // Get low stock products
    getLowStockProducts: builder.query<PaginatedStockAlertsResponse, StockAlertFilters | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.page) searchParams.append("page", String(params.page));
        if (params?.limit) searchParams.append("limit", String(params.limit));
        if (params?.includeOutOfStock !== undefined) {
          searchParams.append("includeOutOfStock", String(params.includeOutOfStock));
        }
        const queryString = searchParams.toString();
        return {
          url: `/stock/low-stock${queryString ? `?${queryString}` : ""}`,
          credentials: "include",
        };
      },
      providesTags: ["Stock"],
    }),

    // Get out of stock products
    getOutOfStockProducts: builder.query<PaginatedStockAlertsResponse, { page?: number; limit?: number } | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.page) searchParams.append("page", String(params.page));
        if (params?.limit) searchParams.append("limit", String(params.limit));
        const queryString = searchParams.toString();
        return {
          url: `/stock/out-of-stock${queryString ? `?${queryString}` : ""}`,
          credentials: "include",
        };
      },
      providesTags: ["Stock"],
    }),

    // Get current thresholds
    getStockThresholds: builder.query<{ success: boolean; data: StockThresholds }, void>({
      query: () => ({
        url: "/stock/thresholds",
        credentials: "include",
      }),
      providesTags: ["StockSettings"],
    }),

    // Update thresholds
    updateStockThresholds: builder.mutation<
      { success: boolean; data: StockThresholds; message?: string },
      Partial<StockThresholds>
    >({
      query: (data) => ({
        url: "/stock/thresholds",
        method: "PUT",
        body: data,
        credentials: "include",
      }),
      invalidatesTags: ["StockSettings", "Stock"],
    }),

    // Update single product stock
    updateProductStock: builder.mutation<
      { success: boolean; data: ProductStockAlert; message?: string },
      { id: string; stockQuantity: number }
    >({
      query: ({ id, stockQuantity }) => ({
        url: `/stock/products/${id}`,
        method: "PATCH",
        body: { stockQuantity },
        credentials: "include",
      }),
      invalidatesTags: ["Stock", "Product"],
    }),

    // Bulk update stock
    bulkUpdateStock: builder.mutation<
      { success: boolean; data: ProductStockAlert[]; message?: string },
      StockUpdateData[]
    >({
      query: (updates) => ({
        url: "/stock/bulk-update",
        method: "POST",
        body: { updates },
        credentials: "include",
      }),
      invalidatesTags: ["Stock", "Product"],
    }),
  }),
});

export const {
  useGetStockSummaryQuery,
  useGetLowStockProductsQuery,
  useGetOutOfStockProductsQuery,
  useGetStockThresholdsQuery,
  useUpdateStockThresholdsMutation,
  useUpdateProductStockMutation,
  useBulkUpdateStockMutation,
} = stockAlertsApi;
